import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { resendLabel, WidgetClient } from "@redotech/redo-api-client/widget";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { hasReturnBear } from "@redotech/redo-model/integration/returnbear/returnbear-utils";
import { MerchantAppReturn, Return } from "@redotech/redo-model/return";
import { toast } from "@redotech/redo-web/alert";
import { RedoClientContext } from "@redotech/redo-web/client";
import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";

function getErrorMessage(err: unknown): string {
  const anyErr = err as {
    response?: { data?: { description?: string } };
    message?: string;
  };
  const description = anyErr.response?.data?.description;

  if (description?.includes("No HS codes")) {
    return description;
  }

  if (description?.includes("Found inactive addresses")) {
    return "Couldn't send email to this address: recipient is inactive. Please try again later, or resend the label to a different email address.";
  }

  return (
    anyErr.message ||
    "Failed to send email. Please contact our support team to resolve this issue."
  );
}

export interface ResendLabelVariables {
  emailOverride: string;
}

export function useResendLabel(
  return_: Return | MerchantAppReturn,
  widgetSlug?: string,
) {
  const redoClient = useRequiredContext(RedoClientContext);
  const merchantRpcClient = useContext(RedoMerchantRpcClientContext);
  const teamContext = useContext(TeamContext);

  return useMutation({
    mutationFn: async ({ emailOverride }: ResendLabelVariables) => {
      try {
        const hasBlueYonderReturns =
          !!return_.blueYonder?.returns &&
          return_.blueYonder.returns.length > 0;
        const isExternalReturnShippingProvider =
          hasReturnBear(return_) || hasBlueYonderReturns;

        amplitude.logEvent(
          isExternalReturnShippingProvider
            ? "resend-external-return-shipping-provider-label"
            : "resend-label-support",
          { team: teamContext?._id, returnId: return_._id },
        );

        if (isExternalReturnShippingProvider && merchantRpcClient) {
          const result =
            await merchantRpcClient.resendExternalReturnShippingProviderLabel({
              returnId: return_._id,
              emailOverride: emailOverride || undefined,
            });

          if (!result.success) {
            throw new Error(
              result.message || "Failed to send shipping label email",
            );
          }
          return;
        }

        if (widgetSlug || teamContext?.widget_slug) {
          const widgetClient = new WidgetClient(
            redoClient,
            widgetSlug || teamContext!.widget_slug,
          );

          await resendLabel(widgetClient, {
            returnId: return_.id,
            emailOverride,
          });
          return;
        }

        return;
      } catch (err) {
        throw new Error(getErrorMessage(err));
      }
    },
    onSuccess: () => {
      toast("Label sent successfully!", { variant: "success" });
    },
    onError: (error) => {
      const message =
        error instanceof Error && error.message
          ? `Resending label failed: ${error.message}`
          : "Resending label failed";
      toast(message, { variant: "error" });
    },
  });
}
